import { useEffect, useState } from 'react';
import Button from 'react-bootstrap/esm/Button';
import Col from 'react-bootstrap/esm/Col';
import Row from 'react-bootstrap/esm/Row';
import Spinner from 'react-bootstrap/esm/Spinner';
import Form from 'react-bootstrap/Form';
import { Link, useLocation, useParams } from 'react-router-dom';
import { generateStartlist } from '../../../api/Assets';
import { getCompetition, getStartlist } from '../../../api/Competitions';
import {
  toggleStartlist,
  toggleMultipleStartlist,
} from '../../../api/Registrations';
import BulkActionMenu from '../../../components/BulkActionMenu';
import CustomToast, { ToastStatesEnum } from '../../../components/CustomToast';
import DownloadButton from '../../../components/Buttons/DownloadButton';
import EmptyState from '../../../components/EmptyState';
import Loading from '../../../components/Loading';
import ModalCentered from '../../../components/ModalCentered';
import UpdateStartNumberModal from '../../../components/UpdateStartNumberModal';
import PageTitle from '../../../components/PageTitle';
import NavMenu from '../../navMenu/NavMenu';
import CompetitionStatus from '../CompetitionStatus';
import StartlistCard from './StartlistCard';
import StartlistTable from './StartlistTable';
import CardContainer from '../../../components/CardContainer';
import StartNumbersButton from './StartNumbersButton';
import StartNumbersModal from './StartNumbersModal';
import { StartNumbersContextProvider } from '../../../contexts/StartNumbersContext';
import { assignRacingNumbers } from '../../../api/Competitions';

const CompetitionStartlist = () => {
  const [startlist, setStartlist] = useState();
  const [filteredStartlist, setFilteredStartlist] = useState();
  const [competition, setCompetition] = useState();
  const [competitionName, setCompetitionName] = useState('');
  const [competitionRaceClasses, setCompetitionRaceClasses] = useState([]);
  const [
    competitionRacingNumberClassInterval,
    setCompetitionRacingNumberClassInterval,
  ] = useState({});
  const [removeFromStartlistModalShow, setRemoveFromStartlistModalShow] =
    useState(false);
  const [editStartNumberModalShow, setEditStartNumberModalShow] =
    useState(false);
  const [showRemoveParticipantSuccess, setShowRemoveParticipantSuccess] =
    useState(false);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showDownloadFail, setShowDownloadFail] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [search, setSearch] = useState('');
  const [downloadError, setDownloadError] = useState('');
  const [removeMultipleModalShow, setRemoveMultipleModalShow] = useState(false);
  const [startNumbersModalShow, setStartNumbersModalShow] = useState(false);
  const [chosenParticipant, setChosenParticipant] = useState({
    id: '',
    racingNumber: 0,
    raceClassString: '',
    email: '',
  });
  const [batchSelectedParticipants, setBatchSelectedParticipants] = useState(
    []
  );
  const [batchToast, setBatchToast] = useState({
    show: false,
    type: ToastStatesEnum.success,
  });
  const [toastData, setToastData] = useState({
    type: '',
    body: '',
    title: '',
    show: false,
  });

  const { id: competitionId } = useParams();
  const location = useLocation();

  useEffect(() => {
    async function fetchData() {
      const data = await getCompetition(competitionId);
      if (data) {
        setCompetition(data.competition);
        setCompetitionName(data.competition.basicInformation?.name);
        setCompetitionRaceClasses(data.competition.regulation?.raceClasses);
        setCompetitionRacingNumberClassInterval(
          data.competition.racingNumberClassIntervals
        );
      }

      const startlist = await getStartlist(competitionId);

      if (startlist.statusCode === 200) {
        setStartlist(startlist.startlist);
        setLoading(false);
        setUpdating(false);
      }
    }

    fetchData();
  }, [
    removeFromStartlistModalShow,
    startNumbersModalShow,
    editStartNumberModalShow,
  ]);

  useEffect(() => {
    if (startlist) {
      filterStartlist(search);
    }
  }, [startlist, search]);

  const filterStartlist = (value) => {
    const fullStartlist = startlist;
    const filtered = fullStartlist.filter(
      (s) =>
        s.name?.toLowerCase().includes(value.toLowerCase()) ||
        s.clubAffiliation?.toLowerCase().includes(value.toLowerCase()) ||
        s.raceClass?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredStartlist(filtered);
  };

  const removeFromStartlist = async () => {
    setUpdating(true);
    let inStartlist = false;
    const status = await toggleStartlist(
      competitionId,
      chosenParticipant.id,
      chosenParticipant.email,
      inStartlist
    );

    if (status === 200) {
      setChosenParticipant({
        id: '',
        racingNumber: 0,
        raceClassString: '',
        email: '',
      });
      setRemoveFromStartlistModalShow(false);
      setShowRemoveParticipantSuccess(true);
    }
  };

  const onDownloadStartlist = async () => {
    setDownloading(true);
    const result = await generateStartlist(competitionId);
    if (typeof result === 'object' && result !== null) {
      const url = window.URL.createObjectURL(new Blob([result]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Startlista.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      setDownloading(false);
    } else if (typeof result === 'string') {
      setDownloadError(result);
      setShowDownloadFail(true);
      setDownloading(false);
    }
  };

  const onEditStartNumber = async (requestednumber) => {
    const success = await assignRacingNumbers(
      competitionId,
      requestednumber,
      chosenParticipant.email
    );
    if (success) {
      setToastData({
        type: 'success',
        body: 'Startnummer har tilldelats',
        title: 'Dina ändringar är sparade',
        show: true,
      });
    } else {
      setToastData({
        type: 'danger',
        body: 'Något gick fel',
        title: 'Startnummer kunde ej sparas',
        show: true,
      });
    }
  };

  const goToRegistrationsBtn = (
    <Link
      to={location.pathname.replace('startlist', 'registrations')}
      className="text-reset text-decoration-none"
    >
      <Button variant="outline-primary" className="d-flex align-items-center">
        Till anmälningar
      </Button>
    </Link>
  );

  const confirmRemovalBtn = (
    <>
      {updating ? (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            className="me-2"
          />
          <span className="visually-hidden">Loading...</span>
          Ta bort
        </>
      ) : (
        'Ta bort'
      )}
    </>
  );

  return (
    <div>
      <Row>
        <Col>
          <div className="d-flex flex-row gap-4 gap-md-4 gap-lg-5">
            <div className="d-flex align-items-center">
              <PageTitle
                title={competitionName}
                includeLinkBack={true}
                linkBackUrl="/organizer/competitions"
                includeStatus={
                  <CompetitionStatus
                    status={competition?.status}
                    id={competitionId}
                  />
                }
              />
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <NavMenu competition={competition} />
        </Col>
      </Row>
      <Row>
        <Col>
          <h4>Startlista</h4>
          <p className="text-secondary">Lista över tävlande.</p>
        </Col>
        <Col
          xs={12}
          md="auto"
          className="d-flex flex-column flex-md-row align-items-md-start gap-2"
        >
          <StartNumbersButton onClick={() => setStartNumbersModalShow(true)}>
            Välj startnummer
          </StartNumbersButton>
          <DownloadButton
            text="Ladda ner startlista"
            loading={downloading}
            onClick={onDownloadStartlist}
            disabled={!startlist?.length}
          />
        </Col>
      </Row>
      <Row className="mt-4">
        <Col md={5} lg={4} xxl={3} className="d-flex justify-content-between">
          <Form.Control
            className="rounded-pill mt-4 mt-md-0"
            placeholder="Sök"
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
      </Row>
      <Row>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center mt-5">
            <Loading size="100px" text="Laddar anmälningar" />
          </div>
        ) : filteredStartlist && filteredStartlist.length > 0 ? (
          <>
            {' '}
            <Col className="d-none d-lg-flex flex-column mt-2">
              <StartlistTable
                startlist={filteredStartlist}
                status={competition?.status}
                setChosenParticipant={setChosenParticipant}
                competitionRacingNumberClassInterval={
                  competitionRacingNumberClassInterval
                }
                onRemove={() => setRemoveFromStartlistModalShow(true)}
                onEdit={() => setEditStartNumberModalShow(true)}
                setBatchSelectedParticipants={setBatchSelectedParticipants}
                batchSelectedParticipants={batchSelectedParticipants}
                rowsPerPage={20}
              />
            </Col>
            <Col className="d-flex flex-column gap-3 d-lg-none mt-4">
              <CardContainer
                rowsPerPage={20}
                batchSelectedElements={batchSelectedParticipants}
                data={startlist}
                setBatchSelectedElements={setBatchSelectedParticipants}
                setData={setStartlist}
                useCheckboox={competition?.status === 'Kommande' || 'Pågående'}
                cardDataPropName="startlistItem"
                cards={filteredStartlist?.map((s, index) => {
                  return (
                    <StartlistCard
                      key={index}
                      startlistItem={s}
                      status={competition?.status}
                      setChosenParticipant={setChosenParticipant}
                      onRemove={() => setRemoveFromStartlistModalShow(true)}
                      onEdit={() => setEditStartNumberModalShow(true)}
                      setBatchSelectedParticipants={
                        setBatchSelectedParticipants
                      }
                      batchSelectedParticipants={batchSelectedParticipants}
                    />
                  );
                })}
              />
            </Col>
          </>
        ) : startlist.length === 0 ? (
          <EmptyState
            headingText="Inga tävlande i startlistan"
            bodyText="Lägg till anmälningar i startlistan när startavgiften är betald och licensen är kontrollerad."
            action={goToRegistrationsBtn}
          />
        ) : (
          <EmptyState
            headingText={'Din sökning gav inga träffar'}
            bodyText={
              'Kontrollera att stavningen är rätt. Du kan söka efter namn, klubb samt tävlingsklass'
            }
          />
        )}
      </Row>

      {competitionRacingNumberClassInterval && (
        <UpdateStartNumberModal
          onEditStartNumber={onEditStartNumber}
          currentStartNumber={chosenParticipant.racingNumber}
          currentCategory={chosenParticipant.raceClassString}
          show={editStartNumberModalShow}
          onHide={() => setEditStartNumberModalShow(false)}
          competitionRacingNumberClassInterval={
            competitionRacingNumberClassInterval
          }
        />
      )}

      <ModalCentered
        header="Ta bort från startlista"
        body={
          <span>
            Är du säker på att du vill ta bort deltagaren från startlistan?
          </span>
        }
        confirmBtnText={confirmRemovalBtn}
        onConfirm={() => {
          removeFromStartlist();
        }}
        show={removeFromStartlistModalShow}
        onHide={() => setRemoveFromStartlistModalShow(false)}
      />

      <ModalCentered
        header="Ta bort flera från startlista"
        body={
          <span>
            Är du säker på att du vill ta bort valda deltagare från startlistan?
          </span>
        }
        confirmBtnText={'Ta bort'}
        onConfirm={async () => {
          const result = await toggleMultipleStartlist(
            competitionId,
            batchSelectedParticipants,
            false
          );

          if (result.statusCode === 200) {
            setBatchToast({ show: true, type: ToastStatesEnum.success });
            setStartlist(result.registrations);
            setBatchSelectedParticipants([]);
          } else {
            setBatchToast({ show: true, type: ToastStatesEnum.error });
          }

          setRemoveMultipleModalShow(false);
        }}
        show={removeMultipleModalShow}
        onHide={() => setRemoveMultipleModalShow(false)}
      />

      <StartNumbersContextProvider
        startList={startlist}
        availableRaceClasses={competitionRaceClasses}
        racingNumberClassIntervals={competitionRacingNumberClassInterval}
      >
        <StartNumbersModal
          show={startNumbersModalShow}
          onHide={() => setStartNumbersModalShow(false)}
          competition={competition}
        />
      </StartNumbersContextProvider>

      <CustomToast
        type="success"
        title="Deltagare borttagen"
        body="Deltagaren har tagits bort från startlistan"
        show={showRemoveParticipantSuccess}
        onClose={() => setShowRemoveParticipantSuccess(false)}
      />
      <CustomToast
        type="danger"
        title="Misslyckad hämtning"
        body={downloadError}
        show={showDownloadFail}
        onClose={() => setShowDownloadFail(false)}
      />
      <CustomToast
        type="success"
        title="Deltagare borttagna"
        body="Deltagarna har tagits bort"
        show={batchToast.show && batchToast.type === ToastStatesEnum.success}
        onClose={() => setBatchToast({ ...batchToast, show: false })}
      />
      <CustomToast
        type="danger"
        title="Deltagare ej borttagna"
        body="Deltagarna kunde inte tas bort"
        show={batchToast.show && batchToast.type === ToastStatesEnum.error}
        onClose={() => setBatchToast({ ...batchToast, show: false })}
      />
      <CustomToast
        type={toastData.type}
        body={toastData.body}
        title={toastData.title}
        show={toastData.show}
        onClose={() =>
          setToastData({ type: '', body: '', title: '', show: false })
        }
      />

      <BulkActionMenu
        show={batchSelectedParticipants.length > 0}
        useDeleteBtn={true}
        selectedElements={batchSelectedParticipants}
        onDelete={() => setRemoveMultipleModalShow(true)}
        allElementsCount={startlist?.length}
        onSelectAll={() => setBatchSelectedParticipants(startlist)}
        onClose={() => setBatchSelectedParticipants([])}
      />
    </div>
  );
};

export default CompetitionStartlist;
